import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { MapPin, Clock, Calendar, AlertCircle, X, Upload, Plus, Trash2, Search, Wrench } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const ZoneCalendarPage = () => {
  const [calendar, setCalendar] = useState([]);
  const [zones, setZones] = useState([]);
  const [selectedZone, setSelectedZone] = useState("");
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [fixingYear, setFixingYear] = useState(false);
  const [message, setMessage] = useState(null);
  const [assignModal, setAssignModal] = useState(null);
  const [schedulingOrders, setSchedulingOrders] = useState([]);
  const [orderSearch, setOrderSearch] = useState("");

  // Global city search
  const [globalCitySearch, setGlobalCitySearch] = useState("");

  // City management
  const [manageCitiesZone, setManageCitiesZone] = useState(null);
  const [newCityName, setNewCityName] = useState("");
  const [addingCity, setAddingCity] = useState(false);
  const [citySearch, setCitySearch] = useState("");

  // Dates for the currently-open zone panel (dates only, no time slots).
  const [zoneDates, setZoneDates] = useState([]);
  const [zoneDatesLoading, setZoneDatesLoading] = useState(false);

  const fetchZones = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/zones`);
      setZones(res.data);
    } catch (err) { console.error(err); }
  }, []);

  const fetchCalendar = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (selectedZone) params.zone_id = selectedZone;
      const res = await axios.get(`${API}/scheduling/zone-calendar`, { params });
      setCalendar(res.data.calendar || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [selectedZone]);

  useEffect(() => { fetchZones(); }, [fetchZones]);
  useEffect(() => { fetchCalendar(); }, [fetchCalendar]);

  const handleSeed = async () => {
    setSeeding(true);
    try {
      const res = await axios.post(`${API}/zones/seed`);
      setMessage({ type: "success", text: `Seeded ${res.data.zones} zones, ${res.data.schedule_entries} schedule dates, ${res.data.cities} cities` });
      fetchZones();
      fetchCalendar();
    } catch (err) {
      setMessage({ type: "error", text: err.response?.data?.detail || "Seed failed" });
    } finally { setSeeding(false); }
  };

  // ONE-CLICK fix for the Prod bug where Jul-Dec dates were saved under year=2027
  // instead of year=2026. Deletes all 2027 rows then re-applies the canonical
  // 2026 Jul-Dec dataset. Idempotent — safe to click multiple times.
  const handleFix2027 = async () => {
    const confirmed = window.confirm(
      "This will DELETE all 2027 zone dates and re-apply the correct 2026 Jul-Dec dates.\n\n" +
      "Safe to click — it only touches 2027 rows and re-applies the 2026 canonical dataset.\n\n" +
      "Continue?"
    );
    if (!confirmed) return;
    setFixingYear(true);
    setMessage(null);
    try {
      const purge = await axios.post(`${API}/admin/appt-dates/purge-year`, null, {
        params: { year: 2027, dry_run: false },
      });
      const apply = await axios.post(`${API}/admin/appt-dates/apply`, null, {
        params: { dry_run: false },
      });
      const deleted = purge.data?.deleted ?? 0;
      const afterTotal = apply.data?.after?.jul_dec_entries ?? "?";
      setMessage({
        type: "success",
        text: `Fixed! Deleted ${deleted} ghost 2027 rows. Jul-Dec 2026 now has ${afterTotal} entries. Reloading calendar...`,
      });
      setManageCitiesZone(null);
      await fetchZones();
      await fetchCalendar();
    } catch (err) {
      setMessage({
        type: "error",
        text: `Fix failed: ${err.response?.data?.detail || err.message}. Are you logged in as Admin?`,
      });
    } finally {
      setFixingYear(false);
    }
  };

  const openAssignModal = async (zoneId, date, window) => {
    setAssignModal({ zoneId, date, window });
    try {
      const res = await axios.get(`${API}/scheduling/orders`);
      const filtered = res.data.filter(o =>
        !o.appointment_window && (!o.zone_id || o.zone_id === zoneId)
      );
      setSchedulingOrders(filtered);
    } catch (err) { console.error(err); }
  };

  const handleAssign = async (orderId) => {
    if (!assignModal) return;
    try {
      await axios.post(`${API}/scheduling/assign-window`, {
        order_id: orderId,
        zone_id: assignModal.zoneId,
        zone_date: assignModal.date,
        window: assignModal.window,
      });
      setMessage({ type: "success", text: "Order scheduled" });
      setAssignModal(null);
      fetchCalendar();
    } catch (err) {
      setMessage({ type: "error", text: err.response?.data?.detail || "Scheduling failed" });
    }
  };

  // City management
  const openCityManager = (zone) => {
    setManageCitiesZone(zone);
    setNewCityName("");
    setCitySearch("");
  };

  // Fetch dates for the active zone panel (dates only — no time slots).
  useEffect(() => {
    if (!manageCitiesZone) { setZoneDates([]); return; }
    let cancelled = false;
    (async () => {
      setZoneDatesLoading(true);
      try {
        const res = await axios.get(`${API}/scheduling/zone-calendar`, {
          params: { zone_id: manageCitiesZone.zone_id },
        });
        if (cancelled) return;
        const cal = res.data.calendar || [];
        // Dedup + sort ascending
        const uniq = Array.from(new Set(cal.map(c => c.date))).sort();
        setZoneDates(uniq);
      } catch (err) {
        console.error(err);
        if (!cancelled) setZoneDates([]);
      } finally {
        if (!cancelled) setZoneDatesLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [manageCitiesZone]);

  const handleAddCity = async () => {
    if (!newCityName.trim() || !manageCitiesZone) return;
    setAddingCity(true);
    try {
      const res = await axios.post(`${API}/zones/${encodeURIComponent(manageCitiesZone.zone_id)}/cities`, { city: newCityName.trim() });
      setMessage({ type: "success", text: res.data.message + (res.data.orders_updated > 0 ? ` (${res.data.orders_updated} orders auto-mapped)` : '') });
      setNewCityName("");
      fetchZones();
    } catch (err) {
      setMessage({ type: "error", text: err.response?.data?.detail || "Failed to add city" });
    } finally {
      setAddingCity(false);
    }
  };

  const handleRemoveCity = async (city) => {
    if (!manageCitiesZone) return;
    try {
      await axios.delete(`${API}/zones/${encodeURIComponent(manageCitiesZone.zone_id)}/cities/${encodeURIComponent(city)}`);
      setMessage({ type: "success", text: `City "${city}" removed` });
      fetchZones();
    } catch (err) {
      setMessage({ type: "error", text: err.response?.data?.detail || "Failed to remove city" });
    }
  };

  // Keep manageCitiesZone in sync with zones data
  const activeManagedZone = manageCitiesZone ? zones.find(z => z.zone_id === manageCitiesZone.zone_id) : null;

  // Group calendar by date
  const groupedByDate = {};
  for (const entry of calendar) {
    if (!groupedByDate[entry.date]) groupedByDate[entry.date] = [];
    groupedByDate[entry.date].push(entry);
  }
  const dates = Object.keys(groupedByDate).sort();

  const filteredOrders = schedulingOrders.filter(o => {
    if (!orderSearch) return true;
    const term = orderSearch.toLowerCase();
    return (
      (o.order_number || "").toLowerCase().includes(term) ||
      (o.city || "").toLowerCase().includes(term) ||
      (o.cliente || "").toLowerCase().includes(term)
    );
  });

  const filteredManagedCities = activeManagedZone
    ? (activeManagedZone.cities || []).filter(c => !citySearch || c.toLowerCase().includes(citySearch.toLowerCase()))
    : [];

  // Global city search results — searches all zones
  const globalCityResults = React.useMemo(() => {
    const term = globalCitySearch.trim().toLowerCase();
    if (!term) return [];
    const results = [];
    for (const z of zones) {
      for (const city of (z.cities || [])) {
        if (city.toLowerCase().includes(term)) {
          results.push({ city, state: z.state, zone_id: z.zone_id });
        }
      }
    }
    results.sort((a, b) => a.city.localeCompare(b.city));
    return results;
  }, [globalCitySearch, zones]);

  return (
    <div className="space-y-5" data-testid="zone-calendar-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2.5">
          <MapPin className="h-5 w-5 text-[#B08A3E]" />
          <h1 className="text-xl font-bold text-gray-800" data-testid="zone-calendar-title">Zone Calendar</h1>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={selectedZone}
            onChange={e => setSelectedZone(e.target.value)}
            className="px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm text-[#1F2937] focus:border-[#D1A054] focus:outline-none"
            data-testid="zone-filter-select"
          >
            <option value="">All Zones</option>
            {zones.map(z => (
              <option key={z.zone_id} value={z.zone_id}>{z.zone_id} ({z.cities.length} cities)</option>
            ))}
          </select>
          <button
            onClick={handleSeed}
            disabled={seeding}
            className="px-3 py-2 bg-[#2F5D8A] hover:bg-[#3B6D9C] text-white text-sm font-medium rounded-lg flex items-center gap-1.5 disabled:opacity-50"
            data-testid="seed-zones-btn"
          >
            <Upload className="h-3.5 w-3.5" />
            {seeding ? "Seeding..." : "Seed Zones"}
          </button>
          <button
            onClick={handleFix2027}
            disabled={fixingYear}
            title="Deletes all 2027 zone dates and re-applies the correct 2026 Jul-Dec dataset"
            className="px-3 py-2 bg-[#B03A2E] hover:bg-[#922B21] text-white text-sm font-semibold rounded-lg flex items-center gap-1.5 disabled:opacity-50"
            data-testid="fix-2027-dates-btn"
          >
            <Wrench className="h-3.5 w-3.5" />
            {fixingYear ? "Fixing..." : "Fix 2027 → 2026"}
          </button>
        </div>
      </div>

      {/* ── Find City / Zone ── */}
      <div className="bg-white rounded-lg border border-gray-200 p-4" data-testid="city-zone-search-section">
        <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-widest mb-2">Find City / Zone</label>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            value={globalCitySearch}
            onChange={e => setGlobalCitySearch(e.target.value)}
            placeholder="Type a city name..."
            className="w-full pl-10 pr-8 py-2.5 bg-gray-50 border border-gray-300 rounded-lg text-sm text-[#1F2937] placeholder-gray-400 focus:border-[#2F5D8A] focus:ring-1 focus:ring-[#2F5D8A]/20 focus:outline-none"
            data-testid="global-city-search-input"
          />
          {globalCitySearch && (
            <button onClick={() => setGlobalCitySearch("")} className="absolute right-2.5 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-gray-200" data-testid="clear-city-search">
              <X className="h-3.5 w-3.5 text-gray-400" />
            </button>
          )}
        </div>

        {globalCitySearch.trim() && (
          <div className="mt-3" data-testid="city-search-results">
            {globalCityResults.length === 0 ? (
              <div className="flex items-center gap-2 py-4 justify-center" data-testid="no-city-found">
                <AlertCircle className="h-4 w-4 text-gray-400" />
                <span className="text-sm text-gray-500">No city found</span>
              </div>
            ) : (
              <>
                <p className="text-[10px] text-gray-400 uppercase tracking-widest mb-1.5">{globalCityResults.length} result{globalCityResults.length !== 1 ? 's' : ''}</p>
                <div className="max-h-56 overflow-y-auto space-y-1 rounded-lg border border-gray-100">
                  {globalCityResults.map((r, i) => (
                    <div key={`${r.zone_id}-${r.city}-${i}`} className="flex items-center justify-between px-3 py-2 hover:bg-gray-50 transition-colors" data-testid={`city-result-${i}`}>
                      <div className="flex items-center gap-2 min-w-0">
                        <MapPin className="h-3.5 w-3.5 text-[#2F5D8A] shrink-0" />
                        <span className="text-sm font-medium text-[#1F2937] truncate">{r.city}</span>
                        <span className="text-xs text-gray-400">&mdash;</span>
                        <span className="text-xs font-medium text-gray-500">{r.state}</span>
                      </div>
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-[#DCE4ED] text-[#2F5D8A] whitespace-nowrap">{r.zone_id}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {message && (
        <div className={`p-2.5 rounded-lg flex items-center justify-between text-sm ${message.type === "success" ? "bg-[#A7C4A0]/15 border border-[#A7C4A0]/40 text-[#6B8F62]" : "bg-[#C07070]/12 border border-[#C07070]/30 text-[#8B4545]"}`}>
          <span>{message.text}</span>
          <button onClick={() => setMessage(null)}><X className="h-3.5 w-3.5" /></button>
        </div>
      )}

      {/* ── Zone City Management Grid ── */}
      <div className="bg-white rounded-lg border border-gray-200 p-4" data-testid="zone-city-management">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-[11px] font-semibold text-gray-500 uppercase tracking-widest">Zone City Mapping</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {zones.map(z => (
            <button
              key={z.zone_id}
              onClick={() => openCityManager(z)}
              className={`text-left px-3 py-2 rounded-lg border transition-all ${
                activeManagedZone?.zone_id === z.zone_id
                  ? 'bg-[#2F5D8A]/10 border-[#2F5D8A]/30 ring-1 ring-[#2F5D8A]/25'
                  : 'bg-gray-50/40 border-gray-200 hover:border-gray-300'
              }`}
              data-testid={`zone-manage-${z.zone_id}`}
            >
              <p className="text-sm font-medium text-[#1F2937]">{z.zone_id}</p>
              <p className="text-[10px] text-gray-500">{z.cities.length} cities &middot; {z.state}</p>
            </button>
          ))}
        </div>
      </div>

      {/* ── City Manager Panel ── */}
      {activeManagedZone && (
        <div className="bg-white rounded-lg border border-[#2F5D8A]/25 p-4" data-testid="city-manager-panel">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-[#2F5D8A]" />
              <h2 className="text-sm font-semibold text-[#1F2937]">{activeManagedZone.zone_id} — Cities</h2>
              <span className="text-[10px] text-gray-500">({activeManagedZone.cities.length} total)</span>
            </div>
            <button onClick={() => setManageCitiesZone(null)} className="p-1 rounded hover:bg-gray-100" data-testid="close-city-manager">
              <X className="h-4 w-4 text-gray-500" />
            </button>
          </div>

          {/* Add city */}
          <div className="flex gap-2 mb-3">
            <input
              type="text"
              value={newCityName}
              onChange={e => setNewCityName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleAddCity()}
              placeholder="Add new city..."
              className="flex-1 px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-sm text-[#1F2937] placeholder-gray-400 focus:border-[#2F5D8A] focus:outline-none"
              data-testid="add-city-input"
            />
            <button
              onClick={handleAddCity}
              disabled={addingCity || !newCityName.trim()}
              className="px-3 py-2 bg-[#2F5D8A] hover:bg-[#3B6D9C] text-white text-sm font-medium rounded-lg flex items-center gap-1 disabled:opacity-40"
              data-testid="add-city-btn"
            >
              <Plus className="h-3.5 w-3.5" />
              {addingCity ? "Adding..." : "Add"}
            </button>
          </div>

          {/* Search cities */}
          {activeManagedZone.cities.length > 10 && (
            <div className="relative mb-2">
              <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-gray-400" />
              <input
                type="text"
                value={citySearch}
                onChange={e => setCitySearch(e.target.value)}
                placeholder="Filter cities..."
                className="w-full pl-8 pr-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-xs text-[#1F2937] placeholder-gray-400 focus:border-gray-300 focus:outline-none"
                data-testid="city-search-input"
              />
            </div>
          )}

          {/* City list */}
          <div className="max-h-48 overflow-y-auto space-y-1" data-testid="city-list">
            {filteredManagedCities.map(city => (
              <div key={city} className="flex items-center justify-between px-2.5 py-1.5 bg-gray-50/50 rounded-lg group" data-testid={`city-item-${city}`}>
                <span className="text-xs text-gray-600">{city}</span>
                <button
                  onClick={() => handleRemoveCity(city)}
                  className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-[#C07070]/20 text-[#8B4545] transition-opacity"
                  data-testid={`remove-city-${city}`}
                  title={`Remove ${city}`}
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              </div>
            ))}
            {filteredManagedCities.length === 0 && (
              <p className="text-gray-400 text-xs py-2 text-center">
                {citySearch ? "No cities match filter" : "No cities in this zone"}
              </p>
            )}
          </div>

          {/* Scheduled Dates for this zone (dates only, no time slots) */}
          <div className="mt-4 pt-4 border-t border-gray-100" data-testid="zone-dates-block">
            <div className="flex items-center gap-2 mb-2">
              <Calendar className="h-3.5 w-3.5 text-[#B08A3E]" />
              <h3 className="text-[11px] font-semibold text-gray-500 uppercase tracking-widest">Scheduled Dates</h3>
              <span className="text-[10px] text-gray-400">({zoneDates.length} total)</span>
            </div>
            {zoneDatesLoading ? (
              <p className="text-gray-400 text-xs py-2 text-center">Loading dates...</p>
            ) : zoneDates.length === 0 ? (
              <p className="text-gray-400 text-xs py-2 text-center">No upcoming dates for this zone</p>
            ) : (
              <div className="flex flex-wrap gap-1.5" data-testid="zone-dates-list">
                {zoneDates.map(d => {
                  const dt = new Date(d + "T00:00:00");
                  const label = dt.toLocaleDateString("en-US", { month: "short", day: "numeric" });
                  const year = dt.getFullYear();
                  return (
                    <div
                      key={d}
                      className="px-2.5 py-1 rounded-lg bg-[#DCE4ED] text-[#2F5D8A] text-xs font-medium flex items-center gap-1.5"
                      data-testid={`zone-date-${d}`}
                    >
                      <span>{label}</span>
                      <span className="text-[10px] text-[#2F5D8A]/60">{year}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Calendar Grid ── */}
      {loading ? (
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-[#D1A054]" />
        </div>
      ) : dates.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-10 text-center">
          <MapPin className="h-8 w-8 text-gray-300 mx-auto mb-2" />
          <p className="text-gray-500 text-sm">No zone schedule found. Click "Seed Zones" to import zone data.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {dates.map(date => {
            const entries = groupedByDate[date];
            const dt = new Date(date + "T00:00:00");
            const dayLabel = dt.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
            return (
              <div key={date} className="bg-white rounded-lg border border-gray-200 overflow-hidden" data-testid={`calendar-date-${date}`}>
                <div className="px-4 py-2.5 bg-gray-50/50 border-b border-gray-200 flex items-center gap-2">
                  <Calendar className="h-3.5 w-3.5 text-[#B08A3E]" />
                  <span className="text-sm font-semibold text-[#1F2937]">{dayLabel}</span>
                  <span className="text-[10px] text-gray-400">{date}</span>
                  <span className="ml-auto px-2 py-0.5 rounded-full text-[10px] font-bold bg-gray-100 text-gray-600">
                    {entries.length} zone{entries.length > 1 ? "s" : ""}
                  </span>
                </div>
                <div className="divide-y divide-gray-200">
                  {entries.map(entry => (
                    <div key={entry.zone_id} className="px-4 py-3" data-testid={`cal-entry-${entry.zone_id}-${date}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-[#1F2937]">{entry.zone_id}</span>
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                            entry.total_booked === 0 ? "bg-[#A7C4A0]/20 text-[#6B8F62]" :
                            entry.total_booked >= entry.total_capacity ? "bg-[#F5EDED]0/15 text-[#8B4545]" :
                            "bg-[#D1A054]/15 text-[#B08A3E]"
                          }`}>
                            {entry.total_booked}/{entry.total_capacity} booked
                          </span>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {entry.windows.map(w => {
                          const full = w.available <= 0;
                          return (
                            <button
                              key={w.window}
                              onClick={() => !full && openAssignModal(entry.zone_id, date, w.window)}
                              disabled={full}
                              className={`px-3 py-1.5 rounded-lg border text-xs font-medium transition-all ${
                                full
                                  ? "bg-[#F5EDED]0/5 border-red-500/20 text-[#8B4545] cursor-not-allowed opacity-60"
                                  : "bg-gray-50 border-gray-300 text-[#1F2937] hover:border-[#D1A054] hover:bg-[#D1A054]/5 cursor-pointer"
                              }`}
                              data-testid={`window-btn-${entry.zone_id}-${date}-${w.window.replace(/:/g, "")}`}
                            >
                              <div className="flex items-center gap-1.5">
                                <Clock className="h-3 w-3" />
                                <span>{w.window}</span>
                              </div>
                              <div className={`text-[10px] mt-0.5 ${full ? "text-[#8B4545]" : w.booked > 0 ? "text-[#B08A3E]" : "text-[#6B8F62]"}`}>
                                {w.booked}/{w.max_capacity}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Assign Order Modal */}
      {assignModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" data-testid="assign-modal">
          <div className="bg-white rounded-xl border border-gray-200 w-full max-w-lg max-h-[80vh] flex flex-col">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-[#1F2937]">Schedule Order</p>
                <p className="text-xs text-gray-500 mt-0.5">
                  {assignModal.zoneId} — {assignModal.date} — {assignModal.window}
                </p>
              </div>
              <button onClick={() => setAssignModal(null)} className="p-1 rounded hover:bg-gray-100"><X className="h-4 w-4 text-gray-500" /></button>
            </div>
            <div className="p-3 border-b border-gray-200">
              <input
                type="text"
                placeholder="Search order #, city, client..."
                value={orderSearch}
                onChange={e => setOrderSearch(e.target.value)}
                className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-sm text-[#1F2937] placeholder-gray-400 focus:border-[#D1A054] focus:outline-none"
                data-testid="assign-modal-search"
              />
            </div>
            <div className="flex-1 overflow-y-auto p-2">
              {filteredOrders.length === 0 ? (
                <div className="text-center py-8 text-gray-400 text-sm">No orders available</div>
              ) : (
                <div className="space-y-1">
                  {filteredOrders.slice(0, 30).map(o => (
                    <button
                      key={o.id}
                      onClick={() => handleAssign(o.id)}
                      className="w-full text-left px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors flex items-center justify-between"
                      data-testid={`assign-order-${o.id}`}
                    >
                      <div>
                        <span className="text-sm text-gray-800 font-medium">{o.order_number}</span>
                        <span className="text-xs text-gray-500 ml-2">{o.city}, {o.state}</span>
                        <span className="text-xs text-gray-400 ml-2">{o.cliente}</span>
                      </div>
                      <span className="text-xs text-gray-400">{o.zone_id || "No zone"}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ZoneCalendarPage;
